import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { object, string } from 'yup';
import Select from 'react-select';
import { Label, ErrorMessage, Button, TextField, locationData, extractErrors } from '../Common/Form';

const validationSchema = object({
    country: string().required('The Country field is required'),
    company: string()
        .max(50, 'The Company/Firm name must be 50 characters or less')
        .matches(/[A-Za-z]/, 'The Company/Firm name must contain at least one letter')
        .required('The Company/Firm field is required'),
    Consent_Provided__c: string().required('The Consent Provided field is required'),
    email: string().email('The Business Email address is not valid').required('The Business Email field is required'),
    firstname: string()
        .max(30, 'The First Name must be 30 characters or less')
        .matches(/[A-Za-z]/, 'The First Name must contain at least one letter')
        .required('The First Name field is required'),
    lastname: string()
        .max(50, 'The Last Name must be 50 characters or less')
        .matches(/[A-Za-z]/, 'The Last Name must contain at least one letter')
        .required('The Last Name field is required'),
    title: string()
        .max(100, 'The Job Title must be 100 characters or less')
        .matches(/[A-Za-z]/, 'The Title must contain at least one letter')
        .required('The Job Title field is required'),
});

const countries = Object.values(locationData.countries).map(({ label }) => {
    return { label, value: label };
});

const PardotGate = ({ loginUrl, formAction }) => {
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const form = useRef(null);

    const onSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setIsSubmitting(true);

            const data = Object.fromEntries(new FormData(e.currentTarget.form).entries());

            validationSchema
                .validate(data, { strict: true, abortEarly: false })
                .then(() => {
                    setErrors({});
                    form.current.submit();
                    setIsSubmitting(false);
                })
                .catch((errs) => {
                    setErrors(extractErrors(errs));
                    setIsSubmitting(false);
                });
        },
        [setErrors, setIsSubmitting]
    );

    return (
        <section>
            <div className="detailColumn__card">
                <p>
                    Please provide the following information to access this content. Already a member?
                    <a href={loginUrl} className="link -login -underline -dark">
                        Login
                    </a>
                </p>
                <form
                    method="POST"
                    encType="application/x-www-form-urlencoded"
                    action={formAction}
                    className="form"
                    ref={form}
                >
                    <TextField id="firstname" label="First Name" required />
                    <ErrorMessage message={errors.firstname ?? ''} />
                    <TextField id="lastname" label="Last Name" required />
                    <ErrorMessage message={errors.lastname ?? ''} />
                    <TextField id="company" label="Company/Firm" required />
                    <ErrorMessage message={errors.company ?? ''} />
                    <TextField id="title" label="Job Title" required />
                    <ErrorMessage message={errors.title ?? ''} />
                    <TextField id="email" label="Business Email" required />
                    <ErrorMessage message={errors.email ?? ''} />
                    <div className="select">
                        <Label id="country" text="Country" required />
                        <Select
                            className="v-select select__options vs--single vs--searchable"
                            classNamePrefix="vs"
                            isClearable
                            name="country"
                            placeholder=""
                            options={countries}
                        />
                        <ErrorMessage message={errors.country ?? ''} />
                    </div>
                    <div className="checkboxes">
                        <div className="checkbox">
                            <input
                                title="Consent Provided"
                                id="Consent_Provided__c"
                                name="Consent_Provided__c"
                                type="checkbox"
                                className="checkbox__input"
                                value="true"
                            />
                            <label htmlFor="Consent_Provided__c" className="checkbox__label">
                                By checking this box, you consent to receive emails from Cutter Associates. You may opt
                                out of these emails at any time. For further information, please consult the{' '}
                                <a href="/privacy" target="_blank" rel="noopener">
                                    Privacy Policy
                                </a>{' '}
                                and
                                <a href="/terms-of-use" target="_blank" rel="noopener">
                                    Terms of Use Policy
                                </a>
                            </label>
                        </div>
                    </div>
                    <ErrorMessage message={errors.Consent_Provided__c ?? ''} />
                    <ErrorMessage
                        message={Object.keys(errors).length > 0 ? 'Please correct the invalid fields above.' : ''}
                    />

                    <Button text="Submit" type="submit" onClick={onSubmit} disabled={isSubmitting} />
                </form>
            </div>
        </section>
    );
};

PardotGate.propTypes = {
    loginUrl: PropTypes.string.isRequired,
    formAction: PropTypes.string.isRequired,
};

export default PardotGate;
